import React, { useState, useEffect, useRef, useCallback } from 'react';

const RoomChat = ({
  room,
  token,
  currentUserId,
  onBack,
  onSettings,
  onInvite
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [members, setMembers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch initial messages
  const fetchMessages = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/rooms/${room.id}/messages?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.reverse()); // Oldest first
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, room.id]);

  // Fetch room members
  const fetchMembers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/rooms/${room.id}/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  }, [API_URL, token, room.id]);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    // Connect to room WebSocket - use /api/ prefix for K8s ingress
    const ws = new WebSocket(`${WS_URL}/api/ws/room/${room.id}`);

    ws.onopen = () => {
      console.log('Room WebSocket connected');
      setWsConnected(true);
      
      // Send join event with token
      ws.send(JSON.stringify({ 
        type: 'join', 
        token: token,
        user_id: currentUserId 
      }));
      
      // Ping interval
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping', token: token }));
        }
      }, 30000);
      ws.pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'message':
          setMessages(prev => {
            if (prev.some(m => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
          scrollToBottom();
          break;
          
        case 'user_joined':
          setOnlineUsers(prev => new Set([...prev, data.user_id]));
          break;
          
        case 'user_left':
          setOnlineUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.user_id);
            return newSet;
          });
          break;
          
        case 'typing':
          if (data.user_id !== currentUserId) {
            setTypingUsers(prev => {
              if (!prev.includes(data.user_nickname)) {
                return [...prev, data.user_nickname];
              }
              return prev;
            });
            // Clear typing after 3 seconds
            setTimeout(() => {
              setTypingUsers(prev => prev.filter(n => n !== data.user_nickname));
            }, 3000);
          }
          break;
          
        case 'online_users':
          setOnlineUsers(new Set(data.users));
          break;
          
        default:
          break;
      }
    };

    ws.onclose = () => {
      console.log('Room WebSocket disconnected');
      setWsConnected(false);
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      // Reconnect after 3 seconds
      setTimeout(() => connectWebSocket(), 3000);
    };

    ws.onerror = (error) => {
      console.error('Room WebSocket error:', error);
    };

    wsRef.current = ws;
  }, [WS_URL, room.id, token, currentUserId]);

  // Initialize
  useEffect(() => {
    fetchMessages();
    fetchMembers();
    connectWebSocket();

    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'leave', token: token, user_id: currentUserId }));
        wsRef.current.close();
      }
    };
  }, [fetchMessages, fetchMembers, connectWebSocket, currentUserId]);

  // Scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Send message via WebSocket for real-time delivery
  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    // Send via WebSocket if connected, otherwise use HTTP
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        content: newMessage.trim(),
        token: token
      }));
      setNewMessage('');
      inputRef.current?.focus();
    } else {
      // Fallback to HTTP if WebSocket is not connected
      setSending(true);
      try {
        const response = await fetch(`${API_URL}/api/rooms/${room.id}/messages`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ content: newMessage.trim() })
        });

        if (response.ok) {
          setNewMessage('');
          inputRef.current?.focus();
          // Fetch messages to get the new one
          fetchMessages();
        }
      } catch (error) {
        console.error('Error sending message:', error);
      } finally {
        setSending(false);
      }
    }
  };

  // Send typing indicator
  const sendTypingIndicator = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', token: token }));
    }
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Astăzi';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ieri';
    }
    return date.toLocaleDateString('ro-RO', { day: 'numeric', month: 'long' });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.created_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  return (
    <div className="flex-1 flex flex-col h-full" data-testid="room-chat">
      {/* Room Header */}
      <div className="bg-gray-800/50 p-4 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-700 rounded-full transition-colors"
              data-testid="room-back-button"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-lg">{room.visibility === 'private' ? '🔒' : '🌍'}</span>
                <h1 className="text-xl font-bold text-white">{room.name}</h1>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <span>{members.length} membri</span>
                <span>•</span>
                <span className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                  {onlineUsers.size} online
                </span>
                {!wsConnected && (
                  <>
                    <span>•</span>
                    <span className="text-yellow-500 flex items-center">
                      <span className="w-2 h-2 bg-yellow-500 rounded-full mr-1 animate-pulse"></span>
                      Se reconectează...
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Members button */}
            <button
              onClick={() => setShowMembers(!showMembers)}
              className={`p-2 rounded-lg transition-colors ${showMembers ? 'bg-cyan-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
              title="Membri"
              data-testid="room-members-button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </button>

            {/* Invite button */}
            {room.visibility === 'private' && onInvite && (
              <button
                onClick={onInvite}
                className="p-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
                title="Invită membri"
                data-testid="room-invite-button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </button>
            )}

            {/* Settings button */}
            {onSettings && (
              <button
                onClick={onSettings}
                className="p-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
                title="Setări cameră"
                data-testid="room-settings-button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Messages Area */}
        <div className="flex-1 flex flex-col">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-500"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-800 flex items-center justify-center">
                    <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">Niciun mesaj încă</h3>
                  <p className="text-gray-500">Fii primul care scrie în această cameră!</p>
                </div>
              </div>
            ) : (
              Object.entries(groupedMessages).map(([date, dateMessages]) => (
                <div key={date}>
                  {/* Date separator */}
                  <div className="flex items-center justify-center my-4">
                    <div className="bg-gray-800 px-3 py-1 rounded-full text-xs text-gray-400">
                      {date}
                    </div>
                  </div>

                  {/* Messages for this date */}
                  {dateMessages.map((msg, idx) => {
                    const isOwn = msg.user_id === currentUserId;
                    const showAvatar = idx === 0 || dateMessages[idx - 1].user_id !== msg.user_id;

                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`}
                        data-testid={`room-message-${msg.id}`}
                      >
                        <div className={`flex max-w-[70%] ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
                          {/* Avatar */}
                          {!isOwn && (
                            <div className={`w-8 h-8 rounded-full flex-shrink-0 mr-2 ${showAvatar ? '' : 'invisible'}`}>
                              {showAvatar && (
                                <div className="w-full h-full rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                  <span className="text-white text-xs font-bold">
                                    {(msg.user_nickname?.[0] || 'U').toUpperCase()}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Message content */}
                          <div>
                            {/* Username (only for others, when avatar shown) */}
                            {!isOwn && showAvatar && (
                              <p className="text-xs text-gray-400 mb-1 ml-1">
                                {msg.user_name || msg.user_nickname}
                              </p>
                            )}
                            <div
                              className={`rounded-2xl px-4 py-2 ${
                                isOwn
                                  ? 'bg-cyan-500 text-white rounded-br-sm'
                                  : 'bg-gray-800 text-white rounded-bl-sm'
                              }`}
                            >
                              <p className="break-words">{msg.content}</p>
                              <p className={`text-xs mt-1 ${isOwn ? 'text-cyan-100' : 'text-gray-500'}`}>
                                {formatTime(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Enhanced Typing indicator */}
          {typingUsers.length > 0 && (
            <div className="px-4 pb-3 animate-fadeIn">
              <div className="inline-flex items-center bg-gradient-to-r from-gray-800/90 to-gray-700/90 backdrop-blur-sm rounded-2xl px-4 py-2.5 border border-cyan-500/30 shadow-lg shadow-cyan-500/10">
                {/* Animated avatar bubbles */}
                <div className="flex -space-x-2 mr-3">
                  {typingUsers.slice(0, 3).map((userName, idx) => (
                    <div 
                      key={idx}
                      className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center border-2 border-gray-800 animate-pulse"
                      style={{ animationDelay: `${idx * 200}ms` }}
                    >
                      <span className="text-white text-xs font-bold">
                        {userName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Typing dots animation */}
                <div className="flex items-center space-x-1.5 mr-2">
                  <div className="w-2.5 h-2.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '0.6s' }}></div>
                  <div className="w-2.5 h-2.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms', animationDuration: '0.6s' }}></div>
                  <div className="w-2.5 h-2.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms', animationDuration: '0.6s' }}></div>
                </div>
                
                {/* User name(s) */}
                <span className="text-cyan-300 font-medium text-sm">
                  {typingUsers.length === 1 
                    ? <><span className="text-white">{typingUsers[0]}</span> scrie...</>
                    : typingUsers.length === 2
                    ? <><span className="text-white">{typingUsers[0]}</span> și <span className="text-white">{typingUsers[1]}</span> scriu...</>
                    : <><span className="text-white">{typingUsers.length}</span> persoane scriu...</>
                  }
                </span>
              </div>
            </div>
          )}

          {/* Message Input */}
          <div className="p-4 pb-20 border-t border-gray-800 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <input
                  ref={inputRef}
                  type="text"
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    sendTypingIndicator();
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Scrie un mesaj..."
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-full text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                  data-testid="room-message-input"
                  disabled={sending}
                />
              </div>
              <button
                onClick={sendMessage}
                disabled={!newMessage.trim() || sending}
                className={`p-3 rounded-full transition-colors ${
                  newMessage.trim() && !sending
                    ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                    : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                }`}
                data-testid="room-send-button"
              >
                {sending ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Members Sidebar */}
        {showMembers && (
          <div className="w-64 border-l border-gray-800 bg-gray-900/50 overflow-y-auto flex-shrink-0">
            <div className="p-4">
              <h3 className="text-white font-semibold mb-4">
                Membri ({members.length})
              </h3>
              <div className="space-y-2">
                {members.map(member => (
                  <div
                    key={member.id}
                    className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-800/50 transition-colors"
                  >
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        {member.avatar_url ? (
                          <img src={member.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <span className="text-white font-bold">
                            {(member.first_name?.[0] || member.nickname?.[0] || 'U').toUpperCase()}
                          </span>
                        )}
                      </div>
                      {/* Online indicator */}
                      {onlineUsers.has(member.id) && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-gray-900 rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">
                        {member.first_name} {member.last_name}
                        {member.id === room.created_by && (
                          <span className="ml-1 text-yellow-500">👑</span>
                        )}
                      </p>
                      <p className="text-gray-500 text-xs truncate">@{member.nickname}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomChat;
